import React, { useState, useEffect } from 'react';
import type { Activity, ActivityType } from '../models/diary';
import { diaryService, ExistingActivity } from '../services/diaryService';
import SearchableSelect from './SearchableSelect';

interface FitnessItemFormProps {
  activities: Activity[];
  patientUserId?: string | number;
  onActivitiesChange: (activities: Activity[]) => void;
  isEditMode?: boolean;
  onDelete?: () => void;
}

const FitnessItemForm: React.FC<FitnessItemFormProps> = ({
  activities,
  patientUserId,
  onActivitiesChange,
  isEditMode = false,
  onDelete
}) => {
  const [activitySelectionMode, setActivitySelectionMode] = useState<'existing' | 'new'>('existing');
  const [existingActivities, setExistingActivities] = useState<ExistingActivity[]>([]);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [selectedExistingActivityIds, setSelectedExistingActivityIds] = useState<number[]>([]);
  const [loadingExistingItems, setLoadingExistingItems] = useState(false);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);

  // Fetch activities and activity types when component mounts
  useEffect(() => {
    if (patientUserId) {
      const fetchData = async () => {
        setLoadingExistingItems(true);
        setLoadingCatalogs(true);
        try {
          const [existingActs, actTypes] = await Promise.all([
            diaryService.getExistingActivities(patientUserId),
            diaryService.getActivityTypes()
          ]);
          setExistingActivities(existingActs);
          setActivityTypes(actTypes);
        } catch (error) {
          console.error('Error fetching activities data:', error);
        } finally {
          setLoadingExistingItems(false);
          setLoadingCatalogs(false);
        }
      };

      fetchData();
    }
  }, [patientUserId]);

  // Initialize selected IDs from activities prop
  useEffect(() => {
    const existingIds = activities
      .filter(a => a.id)
      .map(a => a.id!);
    setSelectedExistingActivityIds(existingIds);
  }, [activities]);

  const handleActivityChange = (activityIndex: number, field: string, value: string | number) => {
    const updatedActivities = [...activities];
    
    if (field === 'activityTypeId') {
      const actType = activityTypes.find(t => t.id === value);
      if (actType) {
        updatedActivities[activityIndex] = {
          ...updatedActivities[activityIndex],
          activityType: actType.name,
          activityTypeId: actType.id
        };
      }
    } else {
      updatedActivities[activityIndex] = {
        ...updatedActivities[activityIndex],
        [field]: value
      };
    }
    
    onActivitiesChange(updatedActivities);
  };

  const addActivity = () => {
    const newActivity: Partial<Activity> = {
      name: '',
      activityType: '',
      details: '',
      activityLevel: '',
      expectedDurationMin: 0
    };
    onActivitiesChange([...activities, newActivity as Activity]);
  };

  const removeActivity = (activityIndex: number) => {
    const updatedActivities = [...activities];
    updatedActivities.splice(activityIndex, 1);
    onActivitiesChange(updatedActivities);
  };

  const handleExistingActivitySelection = (activityId: number) => {
    const existingActivity = existingActivities.find(a => a.id === activityId);
    if (!existingActivity) return;

    const activity: Activity = {
      id: existingActivity.id,
      name: existingActivity.name,
      activityType: existingActivity.activityType,
      details: existingActivity.details,
      activityLevel: existingActivity.activityLevel,
      expectedDurationMin: existingActivity.expectedDurationMin
    };

    onActivitiesChange([...activities, activity]);
    setSelectedExistingActivityIds(prev => [...prev, activityId]);
  };

  const removeExistingActivity = (activityId: number) => {
    const updatedActivities = activities.filter(a => a.id !== activityId);
    onActivitiesChange(updatedActivities);
    setSelectedExistingActivityIds(prev => prev.filter(id => id !== activityId));
  };

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Section Header */}
      <div style={{ 
        marginBottom: 16, 
        paddingBottom: 12, 
        borderBottom: '2px solid #e5e7eb' 
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: 16, 
              fontWeight: 600, 
              color: '#1f2937' 
            }}>
              Actividades Físicas
            </h3>
            <p style={{ 
              margin: '4px 0 0 0', 
              fontSize: 13, 
              color: '#6b7280' 
            }}>
              Agrega actividades físicas al plan del paciente
            </p>
          </div>
          {isEditMode && onDelete && (
            <button 
              type="button"
              onClick={onDelete}
              style={{ 
                background: '#fee2e2', 
                color: '#991b1b', 
                border: 'none', 
                borderRadius: 6, 
                padding: '8px 16px', 
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#fecaca'}
              onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}
            >
              <span>🗑️</span>
              <span>Eliminar</span>
            </button>
          )}
        </div>
      </div>

      {/* Selection Mode Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: 12, 
        marginBottom: 16 
      }}>
        {/* Existing Activities Card */}
        <div
          onClick={() => setActivitySelectionMode('existing')}
          style={{
            border: activitySelectionMode === 'existing' ? '2px solid #38b2ac' : '2px solid #e5e7eb',
            borderRadius: 8,
            padding: 12,
            cursor: 'pointer',
            backgroundColor: activitySelectionMode === 'existing' ? '#f0fdfa' : '#fff',
            transition: 'all 0.2s ease',
            position: 'relative'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <input
              type="radio"
              name="activityMode"
              checked={activitySelectionMode === 'existing'}
              onChange={() => setActivitySelectionMode('existing')}
              style={{ 
                marginRight: 8, 
                marginTop: 2,
                cursor: 'pointer',
                accentColor: '#38b2ac'
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: 14, 
                fontWeight: 600, 
                color: '#1f2937',
                marginBottom: 4 
              }}>
                Usar existente
              </div>
              <div style={{ 
                fontSize: 12, 
                color: '#6b7280',
                lineHeight: '1.4'
              }}>
                Reutiliza una actividad que el paciente ya tiene configurada
              </div>
            </div>
          </div>
        </div>

        {/* New Activity Card */}
        <div
          onClick={() => setActivitySelectionMode('new')}
          style={{
            border: activitySelectionMode === 'new' ? '2px solid #38b2ac' : '2px solid #e5e7eb',
            borderRadius: 8,
            padding: 12,
            cursor: 'pointer',
            backgroundColor: activitySelectionMode === 'new' ? '#f0fdfa' : '#fff',
            transition: 'all 0.2s ease',
            position: 'relative'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <input
              type="radio"
              name="activityMode"
              checked={activitySelectionMode === 'new'}
              onChange={() => setActivitySelectionMode('new')}
              style={{ 
                marginRight: 8, 
                marginTop: 2,
                cursor: 'pointer',
                accentColor: '#38b2ac'
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: 14, 
                fontWeight: 600, 
                color: '#1f2937',
                marginBottom: 4 
              }}>
                Crear nueva
              </div>
              <div style={{ 
                fontSize: 12, 
                color: '#6b7280',
                lineHeight: '1.4'
              }}>
                Configura una nueva actividad personalizada con detalles específicos
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Existing Activities Selection */}
      {activitySelectionMode === 'existing' && (
        <div style={{ 
          backgroundColor: '#f9fafb', 
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: 16,
          marginBottom: 16 
        }}>
          <label style={{ display: 'block' }}>
            <div style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              color: '#374151',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              <span>🏃</span>
              <span>Selecciona una actividad existente</span>
            </div>
            {loadingExistingItems ? (
              <div style={{ 
                padding: 12, 
                fontSize: 13, 
                color: '#6b7280',
                textAlign: 'center',
                backgroundColor: '#fff',
                borderRadius: 6,
                border: '1px dashed #d1d5db'
              }}>
                Cargando actividades del paciente...
              </div>
            ) : existingActivities.length === 0 ? (
              <div style={{ 
                padding: 12, 
                fontSize: 13, 
                color: '#6b7280',
                textAlign: 'center',
                backgroundColor: '#fff',
                borderRadius: 6,
                border: '1px dashed #d1d5db'
              }}>
                El paciente aún no tiene actividades configuradas
              </div>
            ) : (
              <SearchableSelect
                options={existingActivities
                  .filter(activity => !selectedExistingActivityIds.includes(activity.id))
                  .map(activity => ({
                    value: activity.id,
                    label: `${activity.name} - ${activity.activityLevel} (${activity.expectedDurationMin} min)`,
                    searchText: `${activity.name} ${activity.activityLevel} ${activity.activityType}`
                  }))
                }
                value=""
                onChange={(value) => value && handleExistingActivitySelection(Number(value))}
                placeholder="-- Selecciona una actividad --"
                emptyMessage="El paciente aún no tiene actividades configuradas"
              />
            )}
          </label>
        </div>
      )}

      {/* New Activity Creation */}
      {activitySelectionMode === 'new' && (
        <div style={{ 
          backgroundColor: '#f0fdfa', 
          border: '1px solid #99f6e4',
          borderRadius: 8,
          padding: 16,
          marginBottom: 16 
        }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 8
          }}>
            <div style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              <span>✨</span>
              <span>Configura una nueva actividad</span>
            </div>
          </div>
          <p style={{
            margin: '0 0 12px 0',
            fontSize: 12,
            color: '#6b7280',
            lineHeight: '1.5'
          }}>
            Define el tipo de actividad y los detalles específicos para este paciente
          </p>
          <button 
            type="button" 
            onClick={addActivity}
            style={{ 
              background: '#38b2ac', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '8px 16px', 
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#2c9a8f'}
            onMouseLeave={e => e.currentTarget.style.background = '#38b2ac'}
          >
            <span style={{ fontSize: 16 }}>+</span>
            <span>Agregar Nueva Actividad</span>
          </button>
        </div>
      )}

      {/* Display Selected/Created Activities */}
      {activities.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ 
            margin: '0 0 12px 0',
            fontSize: 14,
            fontWeight: 600,
            color: '#374151'
          }}>
            Actividades agregadas ({activities.length})
          </h4>
          
          {activities.map((activity, index) => {
            const isExisting = activity.id && selectedExistingActivityIds.includes(activity.id);
            return (
              <div 
                key={activity.id || index} 
                style={{ 
                  border: isExisting ? '2px solid #3b82f6' : '2px solid #38b2ac',
                  borderRadius: 8, 
                  padding: 16, 
                  marginBottom: 12, 
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                {/* Card Header */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start', 
                  marginBottom: 12,
                  paddingBottom: 12,
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      marginBottom: 6,
                      backgroundColor: isExisting ? '#dbeafe' : '#d1fae5',
                      color: isExisting ? '#1e40af' : '#065f46'
                    }}>
                      {isExisting ? '🏃 EXISTENTE' : '✨ NUEVA'}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}>
                      {activity.name || 'Actividad sin configurar'}
                    </div>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => isExisting ? removeExistingActivity(activity.id!) : removeActivity(index)}
                    style={{ 
                      background: '#fee2e2', 
                      color: '#991b1b', 
                      border: 'none', 
                      borderRadius: 6, 
                      padding: '6px 12px', 
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fecaca'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}
                  >
                    ✕ Eliminar
                  </button>
                </div>
                
                {isExisting ? (
                  // Read-only display for existing activities
                  <div style={{ 
                    backgroundColor: '#f9fafb',
                    padding: 12,
                    borderRadius: 6,
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: 13, marginBottom: 8 }}>
                      <strong style={{ color: '#374151' }}>Tipo:</strong>{' '}
                      <span style={{ color: '#1f2937' }}>
                        {typeof activity.activityType === 'object' ? activity.activityType.name : activity.activityType}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, marginBottom: 8 }}>
                      <strong style={{ color: '#374151' }}>Nivel de intensidad:</strong>{' '}
                      <span style={{ 
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        backgroundColor: activity.activityLevel === 'High' ? '#fee2e2' : 
                                       activity.activityLevel === 'Medium' ? '#fef3c7' : '#dcfce7',
                        color: activity.activityLevel === 'High' ? '#991b1b' : 
                               activity.activityLevel === 'Medium' ? '#92400e' : '#14532d'
                      }}>
                        {activity.activityLevel === 'High' ? 'Alto' : 
                         activity.activityLevel === 'Medium' ? 'Medio' : 'Bajo'}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, marginBottom: 8 }}>
                      <strong style={{ color: '#374151' }}>Duración esperada:</strong>{' '}
                      <span style={{ color: '#1f2937' }}>{activity.expectedDurationMin} minutos</span>
                    </div>
                    {activity.details && (
                      <div style={{ fontSize: 13 }}>
                        <strong style={{ color: '#374151' }}>Detalles:</strong>{' '}
                        <span style={{ color: '#1f2937' }}>{activity.details}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  // Editable fields for new activities
                  <div>
                    {/* Activity Type Selection */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block' }}>
                        <span style={{ 
                          fontSize: 13, 
                          fontWeight: 600, 
                          color: '#374151',
                          marginBottom: 6,
                          display: 'block'
                        }}>
                          1. Tipo de actividad *
                        </span>
                        {loadingCatalogs ? (
                          <div style={{ 
                            padding: 12, 
                            fontSize: 13, 
                            color: '#6b7280',
                            backgroundColor: '#f9fafb',
                            borderRadius: 6,
                            border: '1px dashed #d1d5db',
                            textAlign: 'center'
                          }}>
                            Cargando tipos de actividad...
                          </div>
                        ) : (
                          <SearchableSelect
                            options={activityTypes.map(actType => ({
                              value: actType.id,
                              label: `${actType.name} - ${actType.description}`,
                              searchText: `${actType.name} ${actType.description}`
                            }))}
                            value={activity.activityTypeId || ''}
                            onChange={(value) => value && handleActivityChange(index, 'activityTypeId', Number(value))}
                            placeholder="-- Selecciona el tipo de actividad --"
                            hasError={!activity.activityTypeId}
                          />
                        )}
                      </label>
                    </div>
                    
                    {/* Name */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block' }}>
                        <span style={{ 
                          fontSize: 13, 
                          fontWeight: 600, 
                          color: '#374151',
                          marginBottom: 6,
                          display: 'block'
                        }}>
                          2. Nombre de la actividad *
                        </span>
                        <span style={{
                          fontSize: 12,
                          color: '#6b7280',
                          marginBottom: 8,
                          display: 'block'
                        }}>
                          Dale un nombre descriptivo a esta actividad
                        </span>
                        <input 
                          value={activity.name} 
                          onChange={e => handleActivityChange(index, 'name', e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '10px 12px', 
                            border: activity.name ? '1px solid #d1d5db' : '2px solid #fbbf24',
                            borderRadius: 6, 
                            fontSize: 14,
                            outline: 'none',
                            boxSizing: 'border-box'
                          }} 
                          placeholder="Ej: Carrera matutina en el parque"
                        />
                      </label>
                    </div>
                    
                    {/* Activity Level and Duration - Side by side */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={{ display: 'block' }}>
                          <span style={{ 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: '#374151',
                            marginBottom: 6,
                            display: 'block'
                          }}>
                            3. Intensidad *
                          </span>
                          <SearchableSelect
                            options={[
                              { value: 'Low', label: '🟢 Bajo' },
                              { value: 'Medium', label: '🟡 Medio' },
                              { value: 'High', label: '🔴 Alto' }
                            ]}
                            value={activity.activityLevel}
                            onChange={(value) => handleActivityChange(index, 'activityLevel', String(value))}
                            placeholder="Seleccionar"
                            hasError={!activity.activityLevel}
                          />
                        </label>
                      </div>
                      
                      <div>
                        <label style={{ display: 'block' }}>
                          <span style={{ 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: '#374151',
                            marginBottom: 6,
                            display: 'block'
                          }}>
                            4. Duración (min) *
                          </span>
                          <input 
                            type="number"
                            value={activity.expectedDurationMin || ''} 
                            onChange={e => handleActivityChange(index, 'expectedDurationMin', parseInt(e.target.value) || 0)}
                            style={{ 
                              width: '100%', 
                              padding: '10px 12px', 
                              border: activity.expectedDurationMin ? '1px solid #d1d5db' : '2px solid #fbbf24',
                              borderRadius: 6, 
                              fontSize: 14,
                              outline: 'none',
                              boxSizing: 'border-box'
                            }} 
                            placeholder="30"
                            min="0"
                          />
                        </label>
                      </div>
                    </div>
                    
                    {/* Details */}
                    <div>
                      <label style={{ display: 'block' }}>
                        <span style={{ 
                          fontSize: 13, 
                          fontWeight: 600, 
                          color: '#374151',
                          marginBottom: 6,
                          display: 'block'
                        }}>
                          5. Detalles adicionales (opcional)
                        </span>
                        <span style={{
                          fontSize: 12,
                          color: '#6b7280',
                          marginBottom: 8,
                          display: 'block'
                        }}>
                          Instrucciones específicas, recomendaciones o notas importantes
                        </span>
                        <textarea 
                          value={activity.details} 
                          onChange={e => handleActivityChange(index, 'details', e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '10px 12px', 
                            border: '1px solid #d1d5db',
                            borderRadius: 6, 
                            fontSize: 13, 
                            minHeight: 70, 
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }} 
                          placeholder="Ej: Mantener ritmo constante, llevar agua, usar zapatillas adecuadas"
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FitnessItemForm;
